import React, { useMemo } from 'react';
import { Box, HStack, Progress, Text } from '@chakra-ui/react';
import ReactSelect, { components } from 'react-select';

const CustomOption = ({ children, ...props }) => {
  const { data } = props;
  return (
    <components.Option {...props}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
        <span>{children}</span>
        {data.updated_weight !== undefined && (
           <span style={{
              backgroundColor: '#e9ecef',
              color: '#495057',
              borderRadius: '4px',
              padding: '2px 6px',
              fontSize: '11px',
              fontWeight: '500',
              marginLeft: '8px'
          }}>{data.updated_weight}</span>
        )}
      </div>
    </components.Option>
  );
};

const CustomMenu = (props) => {
  const { currentWeight } = props.selectProps;
  const color = currentWeight >= 10 ? 'red.500' : currentWeight >= 8 ? 'orange.500' : 'gray.500';
  const fontWeight = currentWeight >= 10 ? '600' : '500';

  return (
    <>
      <Box p="8px 12px" borderBottom="1px solid #e9ecef" bg="#f8f9fa">
          <HStack justify="space-between">
              <Text fontSize="13px" color="#495057" fontWeight="500">Select Issues</Text>
              <HStack>
                  <Text fontSize="13px" color={color} fontWeight={fontWeight}>{currentWeight}/10</Text>
                  <Box w="60px">
                      <Progress value={currentWeight * 10} size="xs" colorScheme={currentWeight >= 10 ? 'red' : currentWeight >= 8 ? 'orange' : 'green'} />
                  </Box>
              </HStack>
          </HStack>
      </Box>
      <components.Menu {...props}>
          {props.children}
      </components.Menu>
    </>
  );
};

const ResponseSelector = ({
  response,
  setResponse,
  responseOptions,
  selectedResponse,
  setSelectedResponse,
}) => {
  const styles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '36px',
      backgroundColor: '#ffffff',
      borderColor: state.isFocused ? '#4a90e2' : '#dee2e6',
      borderRadius: '6px',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(74, 144, 226, 0.1)' : 'none',
      '&:hover': { borderColor: '#4a90e2' },
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '4px 8px',
      maxHeight: '120px',
      overflowY: 'auto',
    }),
    menu: (provided) => ({
      ...provided,
      width: '100%',
      zIndex: 10001,
      boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
      borderRadius: '0 0 6px 6px',
      border: '1px solid #dee2e6',
      borderTop: 'none',
      marginTop: '0',
    }),
    menuList: (provided) => ({
      ...provided,
      paddingTop: '4px',
      paddingBottom: '4px',
      maxHeight: '320px',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#4a90e2' : state.isFocused ? '#f8f9fa' : 'white',
      color: state.isSelected ? 'white' : state.isDisabled ? '#adb5bd' : '#495057',
      padding: '6px 8px',
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      borderBottom: '1px solid #f8f9fa',
      fontSize: '14px',
      fontWeight: state.isSelected ? '500' : '400',
      '&:last-child': { borderBottom: 'none' },
      opacity: state.isDisabled ? 0.6 : 1,
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: '#e7f3ff',
      borderRadius: '4px',
      border: '1px solid #b8daff',
      margin: '2px',
      fontSize: '13px',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      fontSize: '13px',
      color: '#0d47a1',
      padding: '2px 6px',
      fontWeight: '400',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: '#0d47a1',
      borderRadius: '0 3px 3px 0',
      ':hover': { backgroundColor: '#dc3545', color: 'white' },
      padding: '2px',
      fontSize: '14px',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#adb5bd',
      fontSize: '14px',
    }),
  };

  const currentWeight = useMemo(() => {
    if (!selectedResponse) return 0;
    return selectedResponse.reduce((sum, opt) => sum + (opt.updated_weight || 0), 0);
  }, [selectedResponse]);

  const selectOptions = useMemo(() => {
    return responseOptions.map(option => ({
      ...option,
      value: option.id,
      label: option.name,
    }));
  }, [responseOptions]);

  const responseValue = useMemo(() => {
    if (selectedResponse) {
      return selectedResponse.map(option => ({
        ...option,
        value: option.id,
        label: option.name,
      }));
    }
    return [];
  }, [selectedResponse]);

  return (
    <ReactSelect
      isMulti
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      options={selectOptions}
      value={responseValue}
      onChange={(selected) => {
        const selection = selected || [];
        const responseIds = selection.map(opt => opt.value).join(',');
        setResponse(responseIds);
        setSelectedResponse(selection);
      }}
      placeholder="Select up to 10 points..."
      isOptionDisabled={(option) => {
        if (!selectedResponse) return false;
        const isAlreadySelected = selectedResponse.some(sel => sel.id === option.id);
        return !isAlreadySelected && currentWeight + (option.updated_weight || 0) > 10;
      }}
      noOptionsMessage={() => "No issues available"}
      styles={styles}
      currentWeight={currentWeight}
      components={{
        DropdownIndicator: () => null,
        IndicatorSeparator: () => null,
        Option: CustomOption,
        Menu: CustomMenu,
      }}
    />
  );
};

export default ResponseSelector;
